export default {
  common: {
    login: 'Login',
    signup: 'Sign up',
    logout: 'Logout',
    history: 'History',
    backToTools: 'Back to all tools',
    pdfGuideLink: 'Read the complete guide to managing PDFs',
  },
  hero: {
    title: 'Every PDF tool you need.',
    subtitle: 'Merge, split, compress, convert, and secure your PDFs — free, and processed on your own server.',
    secure: 'secure and',
    free: 'free',
  },
  trustedBy: { heading: "Join the world's largest companies", subheading: "Employees at these companies use our products" },
  home: {
    searchPlaceholder: 'Search tools… e.g. word to pdf',
    clearSearch: 'Clear search',
    noResults: 'No tools match "{query}". Try a different search.',
    useTool: 'Use tool',
  },
  sidebar: {
    home: 'Home',
    toolsLabel: 'Tools',
    searchPlaceholder: 'Search tools…',
    noToolsFound: 'No tools found.',
  },
  dropzone: {
    dropActive: 'Drop your file(s) here',
    dropIdle: 'Drag & drop file(s) here, or click to browse',
    multiple: 'You can select multiple files',
    single: 'Select one file',
  },
  processing: {
    uploading: 'Uploading & processing…',
    minFiles: 'Please add at least {count} file{plural}.',
  },
  download: {
    ready: 'Your file is ready',
    download: 'Download {filename}',
    another: 'Process another file',
    leaveReview: 'Enjoying it? Leave us a review',
  },
  toolInfo: {
    heading: 'Information',
    howToUse: 'How to use {tool}',
    whyItHelps: 'Why it helps',
    noInstall: 'No installation required',
    privacy: 'Your files stay private',
    everywhere: 'Works on any device',
  },
  faq: {
    heading: 'Frequently asked questions',
  },
  footer: {
    about: 'About',
    contact: 'Contact',
    privacy: 'Privacy Policy',
    terms: 'Terms & Conditions',
    cookiePreferences: 'Cookie preferences',
    rights: 'All rights reserved.',
    reviews: 'Reviews',
    guide: 'PDF Guide',
  },
  notFound: {
    heading: 'Page not found',
    message: "The page you're looking for doesn't exist or has moved.",
    backHome: 'Back to home',
  },
  auth: {
    login: 'Log in',
    email: 'Email',
    password: 'Password',
    forgotPassword: 'Forgot password?',
    noAccount: "Don't have an account?",
    haveAccount: 'Already have an account?',
    signup: 'Sign up',
  },
  categories: {
    'Convert Documents': 'Convert Documents',
    Organize: 'Organize',
    'Edit & Sign': 'Edit & Sign',
    Optimize: 'Optimize',
    'Convert Images': 'Convert Images',
    Security: 'Security',
  },
  tools: {
    'merge-pdf': { title: 'Merge PDF', description: 'Combine multiple PDFs into one document, in the order you choose.', actionLabel: 'Merge PDF' },
    'split-pdf': { title: 'Split PDF', description: 'Extract a page range, or explode every page into its own PDF.', actionLabel: 'Split PDF' },
    'organize-pdf': { title: 'Organize PDF', description: 'Reorder, rotate, or delete pages in a PDF.', actionLabel: 'Save changes' },
    'delete-pdf-pages': { title: 'Delete PDF Pages', description: 'Remove specific pages from a PDF, e.g. "2,4,7-9".', actionLabel: 'Delete pages' },
    'extract-pdf-pages': { title: 'Extract PDF Pages', description: 'Pull specific pages out of a PDF into a new file, e.g. "2,4,7-9".', actionLabel: 'Extract pages' },
    'rotate-pdf': { title: 'Rotate PDF', description: 'Rotate all pages in a PDF by 90, 180, or 270 degrees.', actionLabel: 'Rotate PDF' },
    'compare-pdf': { title: 'Compare PDF', description: 'See a word-level diff between two versions of a PDF, page by page.' },
    'sign-pdf': { title: 'Sign PDF', description: 'Draw, type, or upload a signature and place it on a page.' },
    'edit-pdf': { title: 'Edit PDF', description: 'Add text and images anywhere on a page.' },
    'redact-pdf': { title: 'Redact PDF', description: 'Permanently black out sensitive areas — the underlying content is removed, not just covered.' },
    'flatten-pdf': { title: 'Flatten PDF', description: 'Lock in filled form field values so the PDF can no longer be edited.', actionLabel: 'Flatten PDF' },
    'watermark-pdf': { title: 'Watermark', description: 'Stamp text over every page of a PDF.', actionLabel: 'Add watermark' },
    'page-numbers': { title: 'Add Page Numbers', description: 'Insert page numbers into a PDF, with your choice of position and start number.', actionLabel: 'Add page numbers' },
    'crop-pdf': { title: 'Crop PDF', description: 'Trim margins from every page of a PDF.', actionLabel: 'Crop PDF' },
    'compress-pdf': { title: 'Compress PDF', description: 'Shrink a PDF by re-encoding its structure and downsampling images.', actionLabel: 'Compress PDF' },
    'ocr-pdf': { title: 'OCR PDF', description: 'Make a scanned PDF searchable by recognizing its text (up to 30 pages).', actionLabel: 'Run OCR' },
    'pdf-to-jpg': { title: 'PDF to JPG', description: 'Convert every page of a PDF into a JPG image.', actionLabel: 'Convert to JPG' },
    'image-to-pdf': { title: 'JPG/PNG to PDF', description: 'Combine one or more images into a single PDF.', actionLabel: 'Convert to PDF' },
    'scan-to-pdf': { title: 'Scan to PDF', description: "Use your device's camera to capture pages and combine them into a PDF.", actionLabel: 'Create PDF' },
    'word-to-pdf': { title: 'Word to PDF', description: 'Convert a Word document (.docx) into a PDF.', actionLabel: 'Convert to PDF' },
    'pdf-to-word': { title: 'PDF to Word', description: 'Convert a PDF into an editable Word document (.docx).', actionLabel: 'Convert to Word' },
    'excel-to-pdf': { title: 'Excel to PDF', description: 'Convert an Excel spreadsheet (.xlsx) into a PDF.', actionLabel: 'Convert to PDF' },
    'pdf-to-excel': { title: 'PDF to Excel', description: 'Convert a PDF into an editable Excel spreadsheet (.xlsx).', actionLabel: 'Convert to Excel' },
    'ppt-to-pdf': { title: 'PowerPoint to PDF', description: 'Convert a PowerPoint presentation (.pptx) into a PDF.', actionLabel: 'Convert to PDF' },
    'pdf-to-ppt': { title: 'PDF to PowerPoint', description: 'Convert a PDF into an editable PowerPoint presentation (.pptx).', actionLabel: 'Convert to PowerPoint' },
    'html-to-pdf': { title: 'HTML to PDF', description: 'Convert a web page URL or an HTML file into a PDF.', actionLabel: 'Convert to PDF' },
    'pdf-to-pdfa': { title: 'PDF to PDF/A', description: 'Convert a PDF into the PDF/A archival format for long-term preservation.', actionLabel: 'Convert to PDF/A' },
    'txt-to-pdf': { title: 'TXT to PDF', description: 'Convert a plain text file (.txt) into a PDF.', actionLabel: 'Convert to PDF' },
    'rtf-to-pdf': { title: 'RTF to PDF', description: 'Convert a Rich Text Format file (.rtf) into a PDF.', actionLabel: 'Convert to PDF' },
    'odt-to-pdf': { title: 'ODT to PDF', description: 'Convert an OpenDocument Text file (.odt) into a PDF.', actionLabel: 'Convert to PDF' },
    'ods-to-pdf': { title: 'ODS to PDF', description: 'Convert an OpenDocument Spreadsheet (.ods) into a PDF.', actionLabel: 'Convert to PDF' },
    'odp-to-pdf': { title: 'ODP to PDF', description: 'Convert an OpenDocument Presentation (.odp) into a PDF.', actionLabel: 'Convert to PDF' },
    'csv-to-pdf': { title: 'CSV to PDF', description: 'Convert a CSV spreadsheet (.csv) into a PDF.', actionLabel: 'Convert to PDF' },
    'protect-pdf': { title: 'Protect PDF', description: 'Add a password to a PDF so it can only be opened with the password.', actionLabel: 'Protect PDF' },
    'unlock-pdf': { title: 'Unlock PDF', description: 'Remove password protection from a PDF you know the password for.', actionLabel: 'Unlock PDF' },
    'repair-pdf': { title: 'Repair PDF', description: 'Attempt to fix a corrupted or malformed PDF so it opens normally again.', actionLabel: 'Repair PDF' },
  },
}
